import { Link } from "react-router";

import { Avatar, Icon } from "@/shared/ui";

/**
 * Карточка пользователя в профиле.
 *
 * Орбитальное кольцо вокруг аватара — не украшение ради украшения: это
 * единственный крупный акцент экрана, на котором больше нечему притягивать
 * взгляд. Собрано из трёх слоёв: пунктирные кольца фоном, цветная дуга
 * границей повёрнутого круга и внутренний волосяной обод.
 *
 * Пилюли уровня и кольца прогресса из макета здесь нет: сущности «уровень» в
 * продукте не существует, и нарисовать её значило бы пообещать механику,
 * которой нет.
 *
 * Карточка — ссылка на редактирование имени и аватарки. Каретка справа стоит
 * не для симметрии: без неё по карточке никто не нажимает — именно с этого
 * («сам профиль не открывается когда на себя нажимаешь») и начались правки.
 */
interface ProfileCardProps {
  name: string;
  initial: string;
  avatarUrl: string | null | undefined;
  joined: string;
  /** Куда ведёт нажатие по карточке. */
  to: string;
  /** Подпись ссылки для скринридера — вслух каретка ничего не значит. */
  label: string;
}

export function ProfileCard({ name, initial, avatarUrl, joined, to, label }: ProfileCardProps) {
  return (
    <Link
      to={to}
      aria-label={label}
      data-haptic="select"
      className={[
        "press-row group relative flex items-center gap-4 overflow-hidden rounded-xl p-4",
        "border border-border-strong",
        "bg-[radial-gradient(circle_at_12%_100%,color-mix(in_srgb,var(--color-ai-violet)_10%,transparent),transparent_40%),linear-gradient(135deg,var(--palette-panel2),var(--palette-panel3))]",
      ].join(" ")}
    >
      <span className="relative flex size-[91px] shrink-0 items-center justify-center">
        {/* Пунктирные кольца. */}
        <span
          aria-hidden="true"
          className={[
            "absolute inset-0 rounded-full border border-status-info/25",
            "bg-[repeating-radial-gradient(circle,transparent_0_8px,color-mix(in_srgb,var(--color-text-secondary)_16%,transparent)_8px_9px,transparent_9px_16px)]",
          ].join(" ")}
        />
        {/* Цветная дуга: две противоположные границы окрашены, две прозрачны,
            круг повёрнут — так дуга получается без единого SVG-пути. */}
        <span
          aria-hidden="true"
          className={[
            "absolute -inset-px rotate-[-28deg] rounded-full border-2 border-transparent",
            "border-t-status-profit border-b-ai-violet",
            "drop-shadow-[0_0_3px_color-mix(in_srgb,var(--color-status-profit)_35%,transparent)]",
          ].join(" ")}
        />
        <span aria-hidden="true" className="absolute inset-2.5 rounded-full border border-white/10" />

        {/* Орбитальные частицы. */}
        <span
          aria-hidden="true"
          className="absolute top-[60%] left-[8%] size-1 rounded-full bg-status-profit shadow-[0_0_var(--glow-blur-sm)_var(--color-status-profit)]"
        />
        <span
          aria-hidden="true"
          className="absolute top-[12%] right-[12%] size-1 rounded-full bg-status-info shadow-[0_0_var(--glow-blur-sm)_var(--color-status-info)]"
        />

        <Avatar
          src={avatarUrl}
          initial={initial}
          size={61}
          className="shadow-[inset_0_1px_rgb(255_255_255/0.55)]"
        />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="truncate text-h1 font-bold text-text-bright">{name}</span>
        <span className="flex items-center gap-1.5 text-small font-normal text-text-secondary">
          <Icon name="shieldCheck" size={14} className="shrink-0 text-status-profit" />
          <span className="truncate">{joined}</span>
        </span>
      </div>

      <Icon
        name="caretRight"
        size={16}
        className="shrink-0 text-text-tertiary transition-transform duration-[var(--motion-fast)] ease-out-quint group-active:translate-x-0.5"
      />

      {/* Радужная кромка снизу — она и делает карточку главным объектом экрана. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-px bg-[image:var(--gradient-rainbow)]"
      />
    </Link>
  );
}
